import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Resources.css";

const websites = [
  {
    id: 1,
    name: "E-Secretary KP",
    url: "https://esecretary.kp.gov.pk/",
    description:
      "Official e-Secretary portal for Khyber Pakhtunkhwa government services.",
    icon: "/images/logos/esecretary.jpeg",
    fallback: "🏛️",
    color: "#31516b",
    category: "Government Services",
  },
  {
    id: 2,
    name: "AgriGIS KP",
    url: "https://agrigis.kp.gov.pk/",
    description:
      "Explore agricultural geographic information and mapping resources across KP.",
    icon: "/images/logos/agrigishub.jpeg",
    fallback: "🗺️",
    color: "#218c56",
    category: "Agriculture",
  },
  {
    id: 3,
    name: "Agri Task Force KP",
    url: "http://agritaskforce.kp.gov.pk/",
    description:
      "Agricultural Task Force resources supporting farmers across Khyber Pakhtunkhwa.",
    icon: "/images/logos/agrigistaskforce.jpeg",
    fallback: "👨‍🌾",
    color: "#2675b8",
    category: "Agriculture",
  },
  {
    id: 4,
    name: "Agriculture Department KP",
    url: "https://agriculture.kp.gov.pk/",
    description:
      "Visit the official Agriculture Department website for information and services.",
    icon: "/images/logos/agriculture.png",
    fallback: "🌾",
    color: "#16845a",
    category: "Agriculture",
  },
  {
    id: 5,
    name: "Kissan Bazar KP",
    url: "https://kissanbazar.kp.gov.pk/",
    description:
      "An online marketplace connecting farmers with agricultural products and services.",
    icon: "/images/logos/kissan.png",
    fallback: "🛒",
    color: "#c47727",
    category: "Marketplace",
  },
];

const ResourceIcon = ({ website, hasImageError, onImageError }) => {
  return (
    <div
      className="resource-logo"
      style={{
        "--resource-color": website.color,
      }}
    >
      {hasImageError ? (
        <span className="resource-fallback" aria-hidden="true">
          {website.fallback}
        </span>
      ) : (
        <img
          src={website.icon}
          alt={`${website.name} logo`}
          loading="lazy"
          onError={onImageError}
        />
      )}
    </div>
  );
};

const Resources = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [imageErrors, setImageErrors] = useState({});

  const categories = useMemo(
    () => ["all", ...new Set(websites.map((website) => website.category))],
    [],
  );

  const filteredWebsites = useMemo(
    () =>
      activeCategory === "all"
        ? websites
        : websites.filter((website) => website.category === activeCategory),
    [activeCategory],
  );

  const handleImageError = (websiteId) => {
    setImageErrors((previous) => ({
      ...previous,
      [websiteId]: true,
    }));
  };

  return (
    <main className="resources-page">
      <div className="resources-container">
        {/* Page header */}
        <header className="resources-header">
          <div className="resources-eyebrow">
            {/* <span className="eyebrow-leaf">🌿</span> */}
            AGRICULTURE DEPARTMENT KP
          </div>

          <h1>
            Government <span>Resources</span>
          </h1>

          <p>
            Your gateway to government services, agricultural information,
            geographic resources, and farmer support across Khyber Pakhtunkhwa.
          </p>

          <div className="resources-header-line">
            <span />
          </div>

          <div className="resources-count">
            <span className="count-icon">✦</span>
            {websites.length} useful online resources
          </div>
        </header>

        {/* Category filters */}
        <nav className="resource-filters" aria-label="Filter resources">
          {categories.map((category) => {
            const isActive = activeCategory === category;

            return (
              <button
                key={category}
                type="button"
                className={`resource-filter ${isActive ? "active" : ""}`}
                onClick={() => setActiveCategory(category)}
                aria-pressed={isActive}
              >
                {category === "all" ? "All Resources" : category}
                {category === "all" && (
                  <span className="filter-count">{websites.length}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Resource cards */}
        <section className="resources-grid" aria-label="Available resources">
          {filteredWebsites.map((website, index) => (
            <article
              className="resource-card"
              key={website.id}
              style={{ "--card-index": index }}
            >
              <div className="resource-card-top">
                <ResourceIcon
                  website={website}
                  hasImageError={Boolean(imageErrors[website.id])}
                  onImageError={() => handleImageError(website.id)}
                />

                <span className="resource-number">
                  {String(website.id).padStart(2, "0")}
                </span>
              </div>

              <div className="resource-card-content">
                <span className="resource-category">{website.category}</span>

                <h2>{website.name}</h2>

                <p>{website.description}</p>
              </div>

              <div className="resource-card-footer">
                <span className="resource-link-label">Explore resource</span>

                <a
                  className="resource-visit"
                  href={website.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit ${website.name} (opens in a new tab)`}
                >
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
            </article>
          ))}
        </section>

        {/* Empty state for future use */}
        {filteredWebsites.length === 0 && (
          <div className="resources-empty">
            <span>🌱</span>
            <h2>No resources found</h2>
            <p>Choose another category to explore available resources.</p>
          </div>
        )}

        {/* Return home */}
        <footer className="resources-footer">
          <div className="resources-footer-message">
            {/* <span className="footer-leaf"></span> */}
            <div>
              <h2>Growing a smarter agricultural future</h2>
              <p>Digital access to services for farmers and citizens of KP.</p>
            </div>
          </div>

          <Link to="/" className="resources-home-button">
            <span aria-hidden="true">←</span>
            Back to Home
          </Link>
        </footer>
      </div>
    </main>
  );
};

export default Resources;
